import {
  getIsRefreshingToken,
  getOrganizationId,
  getRefreshToken,
  removeTokens,
  setIsRefreshingToken,
  setOrganizationId,
  setRefreshToken,
} from '../vendor/anaphora-platform/Shared/AnaphoraDashboardCommon/src/services/auth/auth';
import {
  clearLockSessionStorage,
  clearSessionStorage,
  isLocked,
  lockSessionStorage,
} from '../vendor/anaphora-platform/Shared/AnaphoraDashboardCommon/src/services/auth/storage';
import { waitUntil } from '../vendor/anaphora-platform/Shared/AnaphoraDashboardCommon/src/utils/helpers';

export { getAppEntryUrl } from '../vendor/anaphora-platform/Shared/AnaphoraDashboardCommon/src/utils/appEntryUrl';
export { openPendingTab } from '../vendor/anaphora-platform/Shared/AnaphoraDashboardCommon/src/utils/pending-tab';

// The Anaphora platform's session (Shared/AnaphoraDashboardCommon/src/services/auth/AuthService.ts and
// services/applications/ApplicationsService.ts), for sessions that come from the platform. c5mail is served on the
// platform's domain, so it shares the platform's refresh token (cs_refresh_token) and uses it as the platform does:
// the vendored auth.ts decides where it is kept, and every refresh writes the rotated token back there. Each function
// names the platform method it follows. Only the platform issues and refreshes tokens; c5mail sends the access token
// with every request of the session, and its server verifies it (classes/auth.php).

// The platform's VITE_COMMON_API_URL, the same in every environment
const PLATFORM_API_URL = '/core-api';

// The platform's VITE_AUTH_URL: its login and account selector
const PLATFORM_AUTH_URL = '/core/';

// An access token is refreshed this long before it expires, as LaunchPad's AuthService does, so that it is still valid
// when c5mail's server checks it
const ACCESS_TOKEN_EXPIRY_MARGIN = 30 * 1000;

// C5 Mail's application in the platform's ApplicationRegistry, as the platform's VITE_APPLICATION_ID and
// VITE_APPLICATION_API_KEY. The platform only refreshes a token for an application the user is licensed for in the
// account, the license it checks when it launches C5 Mail as well.
export const C5MAIL_APPLICATION_ID = 'f458d7aa-bfca-420b-8349-4276c1dd7cda';
const C5MAIL_APPLICATION_API_KEY = 'cfa9fcb0-46a4-487c-b298-856e703c6183'; // Non-secret key

let initialized = false;
let accessToken = null;
let authorizedApplications = null;
let authorizedApplicationsAccountId = null;
let activeBanners = null;
let activeBannersAccountId = null;

// AuthService.init: a duplicated tab starts with a copy of sessionStorage, and must not share its refresh token. Run on
// first use, so sessions that don't use the platform leave its storage alone.
function init() {
  if (initialized) {
    return;
  }
  initialized = true;

  window.addEventListener('beforeunload', () => {
    clearLockSessionStorage();
  });

  if (isLocked()) {
    clearSessionStorage();
  }
  lockSessionStorage();
}

// jwt-decode, which the platform uses: the token's payload, unverified
function decodeAccessToken(token) {
  let payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  let bytes = Uint8Array.from(atob(payload), (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

// The platform's getAuthHttpClient (api/config.ts): the core API, as the application in X-App-Id
async function requestPlatformApi(method, path, body, bearerToken) {
  let headers = {
    'X-App-Id': C5MAIL_APPLICATION_API_KEY,
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (bearerToken !== undefined) {
    headers['Authorization'] = 'Bearer ' + bearerToken;
  }

  let response = await fetch(PLATFORM_API_URL + path, {
    method: method,
    headers: headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    let error = new Error('Platform API ' + method + ' ' + path + ' failed with ' + response.status);
    error.status = response.status;
    // The platform's ErrorDetailsDto: {errorId, statusCode, message, errorType?, …}
    error.data = await response.json().catch(() => null);
    throw error;
  }

  return await response.json();
}

// AuthService.getCurrentUser
function getCurrentUser() {
  return accessToken ? decodeAccessToken(accessToken) : null;
}

// AuthService.getCurrentOrganizationId
export function getCurrentOrganizationId() {
  return getCurrentUser()?.organization_id;
}

// AuthService.getRefreshOrganizationId
function getRefreshOrganizationId(accountId) {
  return (
    getCurrentOrganizationId() ??
    new URLSearchParams(window.location.search).get('organizationId') ??
    (accountId ? getOrganizationId(accountId) : null) ??
    undefined
  );
}

// AuthService.getAccessToken
async function getAccessToken(accountId) {
  if (!accessToken) {
    return await refreshAccessToken(accountId);
  }

  let decoded = decodeAccessToken(accessToken);
  // If access token still valid return it
  if ((!decoded.exp || decoded.exp * 1000 - ACCESS_TOKEN_EXPIRY_MARGIN > Date.now()) && (!accountId || decoded.account === accountId)) {
    return accessToken;
  }

  // Else try to refresh the access token
  return await refreshAccessToken(accountId ?? decoded.account);
}

// AuthService.removeTokens
function removePlatformTokens() {
  accessToken = null;
  authorizedApplications = null;
  removeTokens();
}

// AuthService.refreshAccessToken. A refusal ends the platform's session as it does there (403 on an invalid refresh
// token, or a password that must be changed), except a 403 for the application: C5 Mail isn't licensed in the account,
// which refuses C5 Mail but not the platform's session that c5mail shares, so the user stays signed in there.
async function refreshAccessToken(accountId) {
  try {
    // Check if token is already refreshing and wait for it
    if (getIsRefreshingToken()) {
      try {
        await waitUntil(() => !getIsRefreshingToken());
      } catch (e) {
        setIsRefreshingToken(false);
      }

      return await getAccessToken(accountId);
    }

    setIsRefreshingToken(true);
    let refreshToken = getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    let token = await requestPlatformApi('POST', '/auth/tokens', {
      refreshToken: refreshToken,
      accountId: accountId,
      organizationId: getRefreshOrganizationId(accountId),
    });
    if (!token.accessToken || !token.refreshToken) {
      removePlatformTokens();
      return null;
    }

    setRefreshToken(token.refreshToken);
    accessToken = token.accessToken;
    let user = getCurrentUser();
    if (user?.account && user.organization_id) {
      setOrganizationId(user.account, user.organization_id);
    }

    authorizedApplications = null;

    return token.accessToken;
  } catch (error) {
    if (error.status === 403 && (error.data?.errorType === 'outdatedPassword' || error.data?.message === 'Invalid refresh token')) {
      removePlatformTokens();
    }
    throw error;
  } finally {
    setIsRefreshingToken(false);
  }
}

// The access token c5mail's server takes with every request of a session from the platform, issued for the account
// (its platform id in lowercase). null when the platform refuses the session (401, 403, or 404 for an account the user
// can no longer reach); a request the platform didn't answer rejects.
export async function getPlatformAccessToken(accountId) {
  init();

  try {
    return await getAccessToken(accountId);
  } catch (error) {
    if (error.status === 401 || error.status === 403 || error.status === 404) {
      return null;
    }
    throw error;
  }
}

// The platform's getAuthRedirectUrl (Shared/AnaphoraDashboardCommon/src/utils/authRedirect.ts), with C5 Mail's address
// as the target
function getAuthRedirectUrl(currentUrl, accountGroupId, exit, organizationId) {
  let baseAuthUrl = window.location.origin + PLATFORM_AUTH_URL;
  if (accountGroupId) {
    baseAuthUrl += accountGroupId + '/';
  }

  let queryParams = new URLSearchParams();
  queryParams.append('redirectTo', currentUrl ?? window.location.origin + window.baseurl + '/{accountId}');
  if (exit) {
    queryParams.append('type', 'exit');
  }
  if (organizationId) {
    queryParams.append('organizationId', organizationId);
  }
  return baseAuthUrl + '?' + queryParams.toString();
}

// Leaving C5 Mail for the platform's account selector, as its Email app's exit button does (Overview.tsx). c5mail
// doesn't know the account's group in the platform, so the selector starts at the organization's top level.
export function getPlatformExitUrl() {
  return getAuthRedirectUrl(undefined, undefined, true, getCurrentOrganizationId());
}

// A session the platform refuses goes to its login, as the platform's auth guards send one (router/auth-guard.ts)
export function leaveToPlatform() {
  history.replaceState(null, '');
  location.href = getAuthRedirectUrl();
}

// ApplicationsService.getAuthorizedApplications, for an account: c5mail can open another account without reloading,
// and the platform's token is for one account. accountId is the account's platform id in lowercase, as in the token.
// Each is the platform's Application model, as the AccountSelector's ApplicationsService.fetchAuthorizedApplications
// makes it: {id, title, icon, launch ('Link' or 'Handoff'), url (the address for the account, undefined for a
// handoff), basePath (undefined for an application on another domain), opensInNewTab}.
export async function getAuthorizedApplications(accountId) {
  if (authorizedApplications !== null && authorizedApplicationsAccountId === accountId) {
    return authorizedApplications;
  }

  init();

  let token = await getAccessToken(accountId);
  if (token === null) {
    return [];
  }

  let applications = await requestPlatformApi('GET', '/application/authorized-applications', undefined, token);
  authorizedApplications = applications.map((dto) => {
    return {
      id: dto.id,
      title: dto.title,
      icon: dto.icon,
      launch: dto.launch,
      url: dto.url ?? undefined,
      basePath: dto.basePath ?? undefined,
      opensInNewTab: dto.opensInNewTab,
    };
  });
  authorizedApplicationsAccountId = accountId;

  return authorizedApplications;
}

// The AccountSelector's ApplicationsService.launchApplication: enters a handoff application in the account, answering
// with where to send the browser. organizationId is the one the user picked. accountId is the account's platform id
// in lowercase, as in the token.
export async function launchApplication(applicationId, accountId, organizationId, language) {
  init();

  let token = await getAccessToken(accountId);
  if (token === null) {
    throw new Error('No platform access token to launch application ' + applicationId);
  }

  let launch = await requestPlatformApi('POST', '/application/' + encodeURIComponent(applicationId) + '/launch', {
    accountId: accountId,
    organizationId: organizationId,
    language: language,
  }, token);

  return launch.redirectUrl;
}

// Banners.activeList, as the platform's BannersLoader calls it (Shared/AnaphoraDashboardCommon/src/components/
// BannersLoader.tsx): the banners the platform shows in the account, its ActiveBannerDto {id, message (HTML), variant,
// scope, isDismissible}: the global, organization and account ones, and those for C5 Mail. Loaded once per account,
// as BannersLoader does. accountId is the account's platform id in lowercase, as in the token.
export async function getActiveBanners(accountId) {
  if (activeBanners !== null && activeBannersAccountId === accountId) {
    return activeBanners;
  }

  init();

  let token = await getAccessToken(accountId);
  if (token === null) {
    return [];
  }

  let query = '?applicationId=' + encodeURIComponent(C5MAIL_APPLICATION_ID);
  activeBanners = await requestPlatformApi('GET', '/banners/active' + query, undefined, token);
  activeBannersAccountId = accountId;

  return activeBanners;
}
